import React, { useEffect, useState } from "react";
import API from "../api";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

const MyOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // 1. Get User from Storage
    const storedUser = localStorage.getItem("user");
    
    if (!storedUser) {
      toast.error("Please login to view orders");
      navigate("/login");
      return;
    }

    const userObj = JSON.parse(storedUser);

    // 2. ROBUST ID CHECK: Try all common variations
    const finalUserId = userObj._id || userObj.userId || userObj.id;

    if (!finalUserId) {
      console.error("❌ ERROR: User ID is missing in localStorage!", userObj);
      toast.error("Session invalid. Please login again.");
      localStorage.removeItem("user");
      navigate("/login");
      return;
    }

    const fetchOrders = async () => {
      try {
        setLoading(true);
        // 3. Send the CORRECT ID
        console.log("Fetching orders for ID:", finalUserId);
        const res = await API.get(`/orders/user/${finalUserId}`);
        setOrders(res.data);
      } catch (err) {
        console.error("Failed to load orders:", err);
        // If 404, it just means no orders found yet
        if (err.response && err.response.status === 404) {
             setOrders([]);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [navigate]);

  if (loading) return (
    <div className="flex flex-col justify-center items-center h-screen text-green-700 font-bold bg-gray-50">
       <div className="animate-spin text-4xl mb-2">⏳</div>
       Loading your history...
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 font-sans pb-20">
      
      {/* 🟢 Header (Fixed Tailwind Classes) */}
      <div className="bg-white shadow-sm p-4 sticky top-0 z-10 flex items-center gap-4 border-b border-gray-100">
        <Link to="/" className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors font-bold text-xl no-underline">
          ←
        </Link>
        <h1 className="text-xl font-extrabold text-gray-800">My Orders 📦</h1>
      </div>

      <div className="max-w-3xl mx-auto p-4 space-y-6">
        
        {/* 🟢 Empty State (Fixed Buttons) */}
        {!loading && orders.length === 0 && (
          <div className="text-center py-20 flex flex-col items-center">
            <div className="text-6xl mb-6 bg-white p-8 rounded-full shadow-sm">🛒</div>
            <h2 className="text-2xl font-bold text-gray-700">No orders yet</h2>
            <p className="text-gray-400 mb-8 mt-2 max-w-xs mx-auto">Looks like you haven't placed an order yet. Start shopping!</p>
            <Link to="/" className="bg-green-600 text-white px-8 py-3 rounded-full shadow-lg hover:bg-green-700 font-bold transition-transform active:scale-95 no-underline">
              Start Shopping
            </Link>
          </div>
        )}

        {/* 🟢 Orders List */}
        {orders.map((order) => (
          <div key={order._id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow">
            
            {/* Card Header */}
            <div className="bg-gray-50/80 p-4 flex justify-between items-center border-b border-gray-100">
              <div>
                <div className="text-[10px] text-gray-400 uppercase font-extrabold tracking-wider">ORDER #{order._id.slice(-6).toUpperCase()}</div>
                <div className="text-xs text-gray-500 font-medium mt-0.5">
                  {new Date(order.createdAt).toLocaleDateString("en-IN", { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
              <span className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-wide font-bold border ${
                order.status === 'Delivered' ? 'bg-green-100 text-green-700 border-green-200' :
                order.status === 'Cancelled' ? 'bg-red-100 text-red-700 border-red-200' :
                'bg-yellow-50 text-yellow-700 border-yellow-200'
              }`}>
                {order.status || "Pending"}
              </span>
            </div>

            {/* Items */}
            <div className="p-4 space-y-4">
              {order.items?.map((item, idx) => (
                <div key={idx} className="flex items-center gap-4">
                   <div className="relative shrink-0">
                     <img 
                       src={item.image || "https://placehold.co/100?text=No+Img"} 
                       alt={item.name}
                       className="w-14 h-14 rounded-xl object-cover bg-gray-100 border border-gray-100" 
                     />
                     <span className="absolute -top-2 -right-2 bg-gray-800 text-white text-[10px] w-5 h-5 flex items-center justify-center rounded-full border-2 border-white shadow-sm font-bold">
                       {item.quantity}
                     </span>
                   </div>
                   <div className="flex-1 min-w-0">
                     <div className="font-bold text-sm text-gray-800 truncate">{item.name}</div>
                     <div className="text-xs text-gray-500">₹{item.price} per unit</div>
                   </div>
                   <div className="font-bold text-sm text-gray-900">₹{item.price * item.quantity}</div>
                </div>
              ))}
            </div>

            {/* Card Footer (Total) */}
            <div className="p-4 border-t border-gray-100 flex justify-between items-center bg-gray-50">
              <div className="text-xs text-gray-500 font-medium">Payment: {order.paymentMethod || "COD"}</div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Total:</span>
                <span className="text-lg font-extrabold text-green-700">₹{order.total}</span>
              </div>
            </div>

          </div>
        ))}
      </div>
    </div>
  );
};

export default MyOrders;